import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ActiveFilters, FilterSection } from '../../layout/filter-sidebar/filter.model';
import { AddToCartEvent, Product } from '../../shared/models/product.model';
import { BreadcrumbComponent, BreadcrumbItem } from '../../shared/ui/breadcrumb/breadcrumb.component';
import { CatalogCategory, CatalogResponse, CatalogSortKey, SubcategoryItem } from './data/catalog.models';
import { Category } from '../../shared/models/category.model';
import { CategoryService } from '../../shared/services/category.service';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  LayoutGrid,
  List,
  LucideIconData,
  SlidersHorizontal,
} from 'lucide-angular';
import { SortDropdownComponent, SortOption } from '../../shared/ui/sort-dropdown/sort-dropdown.component';
import { Subject, catchError, combineLatest, of, switchMap, tap } from 'rxjs';

import { ActiveFiltersBarComponent } from './components/active-filters-bar/active-filters-bar.component';
import { ButtonComponent } from '../../shared/ui/button/button.component';
import { AvailabilityValue, CatalogPreferencesService } from './data/catalog-preferences.service';
import { CatalogService } from './data/catalog.service';
import {
  deriveProductCategorySlug,
  FILTER_SECTION_IDS,
  toSubcategoryItems,
} from './data/catalog-mappers';
import { FilterSidebarComponent } from '../../layout/filter-sidebar/filter-sidebar.component';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { PageSizeSelectComponent } from '../../shared/ui/page-size-select/page-size-select.component';
import { PaginationComponent } from '../../shared/ui/pagination/pagination.component';
import { ProductCardComponent } from '../../shared/ui/product-card/product-card.component';
import { ProductCardSkeletonComponent } from '../../shared/ui/product-card-skeleton/product-card-skeleton.component';
import { ProductZoomModalComponent } from '../../shared/ui/product-zoom-modal/product-zoom-modal.component';
import { SearchBarComponent } from '../../shared/ui/search-bar/search-bar.component';
import { SubcategoriesCarouselComponent } from './components/subcategories-carousel/subcategories-carousel.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CartService } from '../../shared/services/cart.service';
import { CartDrawerService } from '../../shared/services/cart-drawer.service';
import { PrepurchaseModalService } from '../../shared/services/prepurchase-modal.service';
import { StockAlertModalService } from '../../shared/services/stock-alert-modal.service';
import { StockAlertStore } from '../../shared/services/stock-alert.store';
import { ToastService } from '../../shared/services/toast.service';
import { cartErrorMessage } from '../../shared/utils/cart.utils';
import { SeoService } from '../../shared/services/seo.service';
import { buildBreadcrumbJsonLd } from '../../shared/utils/seo.utils';

type ViewMode = 'grid' | 'list';

@Component({
  selector: 'app-catalog-page',
  imports: [
    SearchBarComponent,
    BreadcrumbComponent,
    PaginationComponent,
    SortDropdownComponent,
    PageSizeSelectComponent,
    ProductCardComponent,
    ProductCardSkeletonComponent,
    ProductZoomModalComponent,
    ButtonComponent,
    IconComponent,
    FilterSidebarComponent,
    ActiveFiltersBarComponent,
    SubcategoriesCarouselComponent,
    RouterLink,
  ],
  templateUrl: './catalog-page.component.html',
  styleUrl: './catalog-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatalogPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly catalogService = inject(CatalogService);
  private readonly categoryService = inject(CategoryService);
  private readonly preferences = inject(CatalogPreferencesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cartService = inject(CartService);
  private readonly cartDrawerService = inject(CartDrawerService);
  private readonly prepurchaseModalService = inject(PrepurchaseModalService);
  private readonly stockAlertModal = inject(StockAlertModalService);
  private readonly stockAlertStore = inject(StockAlertStore);
  private readonly toastService = inject(ToastService);
  private readonly seoService = inject(SeoService);

  // Árbol de categorías raíz — reutilizado del cache global del service. Se
  // usa sólo para poblar el carrusel cuando estamos en `/tienda` sin categoría.
  private readonly rootCategoriesSig = toSignal(
    this.categoryService.getRootCategoriesCached(),
    { initialValue: [] as Category[] },
  );

  // ── Icons ──────────────────────────────────────────────────────────────────
  protected readonly gridIcon: LucideIconData = LayoutGrid;
  protected readonly listIcon: LucideIconData = List;
  protected readonly filterIcon: LucideIconData = SlidersHorizontal;

  // ── State signals ──────────────────────────────────────────────────────────
  protected category = signal<string | null>(null);
  protected activeFilters = signal<ActiveFilters>({});
  protected searchQuery = signal('');
  protected currentPage = signal(1);
  protected sortKey = signal<CatalogSortKey>('newest');
  protected perPage = signal(24);
  protected viewMode = signal<ViewMode>('grid');
  protected filterDrawerOpen = signal(false);
  // Flag top-level (no es un `ActiveFilter`) para `/tienda?on_discount=true`.
  // Viaja directo a `CatalogParams.on_discount` y de ahí al query param del
  // endpoint `/avaliable-products?on_discount=1`. Sólo surte efecto en
  // el catálogo general — el endpoint por categoría aún no lo soporta.
  protected onDiscount = signal(false);
  protected loading = signal(true);
  protected error = signal<string | null>(null);
  protected catalogData = signal<CatalogResponse | null>(null);
  protected zoomProduct = signal<Product | null>(null);
  /** Campana del quickview: misma fuente que la card (`StockAlertStore`). */
  protected readonly zoomNotifyActive = computed(() =>
    this.stockAlertStore.hasAlert(this.zoomProduct()?.id),
  );

  private syncing = false;
  private readonly fetch$ = new Subject<void>();

  // ── Derived state ──────────────────────────────────────────────────────────
  protected categoryInfo = computed<CatalogCategory | null>(
    () => this.catalogData()?.category ?? null,
  );

  // Alimenta el <app-subcategories-carousel>: si estamos en una categoría con
  // hijos, usa esos; si estamos en `/tienda` sin categoría, cae a las raíces.
  protected displaySubcategories = computed<SubcategoryItem[]>(() => {
    const fromCategory = this.categoryInfo()?.subcategories;
    if (fromCategory?.length) return fromCategory;
    if (!this.category()) return toSubcategoryItems(this.rootCategoriesSig());
    return [];
  });

  protected showSidebar = computed(() => !!this.category());

  /**
   * Detecta si el componente está sirviendo `/buscar?q=...`. En esa ruta no
   * hay `:category` (por eso `category()` es null) y la URL top-level es
   * literalmente `buscar`. Gobierna H1, breadcrumbs, placeholder y empty state
   * cosméticos — el resto de la página (filtros, sort, paginación) funciona
   * idéntico que en `/tienda` sin categoría.
   */
  protected isSearchMode = computed(
    () => !this.category() && this.route.snapshot.url[0]?.path === 'buscar',
  );

  protected breadcrumbs = computed<BreadcrumbItem[]>(() => {
    if (this.isSearchMode()) {
      return [
        { label: 'Inicio', route: '/' },
        { label: 'Búsqueda', route: null },
      ];
    }
    const items: BreadcrumbItem[] = [
      { label: 'Inicio', route: '/' },
      { label: 'Tienda', route: '/tienda' },
    ];
    const path = this.catalogData()?.categoryPath ?? [];
    path.forEach((node, i) => {
      const isLast = i === path.length - 1;
      items.push({
        label: node.name,
        route: isLast ? null : `/tienda/${node.slug}`,
      });
    });
    return items;
  });

  protected filterSections = computed<FilterSection[]>(
    () => this.catalogData()?.availableFilters ?? [],
  );

  protected products = computed(() => this.catalogData()?.products ?? []);
  protected totalPages = computed(() => this.catalogData()?.pagination.totalPages ?? 1);
  protected totalProducts = computed(() => this.catalogData()?.totalProducts ?? 0);

  protected hasActiveFilters = computed(() => {
    const f = this.activeFilters();
    return Object.entries(f).some(([, v]) => {
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'string') return v !== '' && v !== 'all';
      return false;
    });
  });

  protected skeletonArray = computed(() => {
    const max = this.viewMode() === 'grid' ? 12 : 6;
    return Array.from({ length: Math.min(this.perPage(), max) }, (_, i) => i);
  });

  protected searchPlaceholder = computed(() => {
    if (this.isSearchMode()) return 'Refinar búsqueda...';
    const cat = this.categoryInfo();
    return cat ? `Buscar en ${cat.name}...` : 'Buscar en la tienda...';
  });

  protected sortOptions = computed<SortOption[]>(() => [
    { value: 'newest', label: 'Más recientes' },
    { value: 'price-asc', label: 'Precio: ascendente' },
    { value: 'price-desc', label: 'Precio: descendente' },
    { value: 'name-asc', label: 'Nombre: A/Z' },
    { value: 'name-desc', label: 'Nombre: Z/A' },
    { value: 'popular', label: 'Más vendidos' },
  ]);

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.fetch$.pipe(
      tap(() => {
        this.loading.set(true);
        this.error.set(null);
      }),
      switchMap(() =>
        this.catalogService
          .getCatalogData({
            category: this.category(),
            filters: this.activeFilters(),
            search: this.searchQuery(),
            page: this.currentPage(),
            perPage: this.perPage(),
            sort: this.sortKey(),
            on_discount: this.onDiscount(),
          })
          .pipe(
            catchError((err) => {
              console.error('[catalog] fetch failed', err);
              this.error.set('No pudimos cargar los productos. Intenta de nuevo.');
              return of(null);
            }),
          ),
      ),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      if (data) {
        this.catalogData.set(data);
        this.applySeo();
      }
      this.loading.set(false);
    });

    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, query]) => {
        this.syncing = true;
        // Preferencias (sort/perPage/availability/view) que completan los params
        // cuando no vienen en la query. Se persisten en COOKIE, así que el render
        // SSR las lee igual que el cliente: server y cliente resuelven los mismos
        // params (con cookie → prefs reales en ambos; sin cookie → defaults en
        // ambos), la clave de TransferState de `CatalogService.getCatalogData`
        // hace match y no hay flash tampoco para usuarios recurrentes.
        const prefs = this.preferences.read();

        this.category.set(params.get('category'));
        this.viewMode.set(
          (query.get('view') as ViewMode) || prefs.viewMode || 'grid',
        );
        this.sortKey.set(
          (query.get('sort') as CatalogSortKey) || prefs.sortKey || 'newest',
        );
        this.currentPage.set(Number(query.get('page')) || 1);
        this.perPage.set(
          Number(query.get('perPage')) || prefs.perPage || 24,
        );
        this.searchQuery.set(query.get('q') ?? '');
        this.onDiscount.set(query.get('on_discount') === 'true');

        const filters: ActiveFilters = {};
        query.keys.forEach((key) => {
          if (!FILTER_SECTION_IDS.has(key)) return;
          const val = query.getAll(key);
          filters[key] = val.length === 1 ? val[0] : val;
        });
        if (!('availability' in filters) && prefs.availability) {
          filters['availability'] = prefs.availability;
        }
        this.activeFilters.set(filters);

        this.syncing = false;
        this.fetch$.next();
      });
  }

  // ── SEO ────────────────────────────────────────────────────────────────────
  /**
   * Aplica metadatos SEO según el modo del catálogo. Sobreescribe los defaults
   * estáticos que el `seoResolver` aplicó en `data.seo` con valores derivados
   * del API (nombre real de la categoría, query de búsqueda).
   */
  private applySeo(): void {
    const data = this.catalogData();
    if (!data) return;

    const isSearch = this.isSearchMode();
    const query = this.searchQuery().trim();
    const cat = data.category;
    const total = data.totalProducts;

    let title: string;
    let description: string;
    let robots: 'index,follow' | 'noindex,nofollow' | undefined;

    if (isSearch) {
      title = query
        ? `Resultados para "${query}"`
        : 'Resultados de búsqueda';
      description = query
        ? `Resultados de búsqueda para "${query}" en Perrón Store. ${total} productos encontrados.`
        : 'Busca cartas, sobres y productos TCG en el catálogo completo de Perrón Store.';
      robots = 'noindex,nofollow';
    } else if (cat) {
      title = cat.name;
      const trimmed = cat.description?.trim() ?? '';
      description = trimmed
        ? trimmed
        : `${cat.name} en Perrón Store. ${total} productos disponibles. Cartas TCG, sobres y productos sellados con envío a todo Costa Rica.`;
    } else {
      title = 'Tienda';
      description =
        'Explora todo el catálogo de Perrón Store: cartas TCG, sobres, productos sellados, accesorios y precompras.';
    }

    const canonical = this.buildCanonical();
    const breadcrumbJsonLd = buildBreadcrumbJsonLd(this.breadcrumbs());

    this.seoService.apply({
      title,
      description,
      canonical,
      type: 'website',
      ...(robots ? { robots } : {}),
      jsonLd: breadcrumbJsonLd,
    });
  }

  /**
   * Canonical limpio: sólo path + filtros que cambian el contenido (categoría,
   * búsqueda). Excluimos `view`, `sort`, `page`, `perPage` para evitar cientos
   * de URLs duplicadas en Google.
   */
  private buildCanonical(): string {
    const path = this.router.url.split('?')[0];
    const q = this.searchQuery().trim();
    if (this.isSearchMode() && q) {
      return `${path}?q=${encodeURIComponent(q)}`;
    }
    return path;
  }

  // ── URL sync helper ────────────────────────────────────────────────────────
  private syncUrl(): void {
    if (this.syncing) return;

    const queryParams: Record<string, string | string[]> = {};
    const view = this.viewMode();
    const sort = this.sortKey();
    const page = this.currentPage();
    const q = this.searchQuery();
    const perPage = this.perPage();

    if (view !== 'grid') queryParams['view'] = view;
    if (sort !== 'newest') queryParams['sort'] = sort;
    if (page > 1) queryParams['page'] = String(page);
    if (q) queryParams['q'] = q;
    if (perPage !== 24) queryParams['perPage'] = String(perPage);
    if (this.onDiscount()) queryParams['on_discount'] = 'true';

    for (const [key, value] of Object.entries(this.activeFilters())) {
      if (!value || (Array.isArray(value) && value.length === 0)) continue;
      if (typeof value === 'string' && (value === '' || value === 'all')) continue;
      queryParams[key] = value;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  // ── Event handlers ─────────────────────────────────────────────────────────
  protected onSearch(query: string): void {
    this.searchQuery.set(query);
    this.currentPage.set(1);
    this.syncUrl();
  }

  protected onSortChange(sort: string): void {
    const key = sort as CatalogSortKey;
    this.sortKey.set(key);
    this.currentPage.set(1);
    this.preferences.write({ sortKey: key });
    this.syncUrl();
  }

  protected onPageSizeChange(size: number): void {
    this.perPage.set(size);
    this.currentPage.set(1);
    this.preferences.write({ perPage: size });
    this.syncUrl();
  }

  protected onViewModeChange(mode: ViewMode): void {
    this.viewMode.set(mode);
    this.preferences.write({ viewMode: mode });
    this.syncUrl();
  }

  protected onFiltersChange(filters: ActiveFilters): void {
    this.activeFilters.set(filters);
    this.currentPage.set(1);
    const av = filters['availability'];
    const availability: AvailabilityValue =
      av === 'in_stock' || av === 'out_of_stock' ? av : '';
    this.preferences.write({ availability });
    this.syncUrl();
  }

  protected onRemoveFilter(event: { sectionId: string; value: string }): void {
    const current = { ...this.activeFilters() };
    const sectionVal = current[event.sectionId];

    if (Array.isArray(sectionVal)) {
      const next = sectionVal.filter((v) => v !== event.value);
      if (next.length === 0) {
        delete current[event.sectionId];
      } else {
        current[event.sectionId] = next;
      }
    } else {
      delete current[event.sectionId];
    }

    this.activeFilters.set(current);
    this.currentPage.set(1);
    if (event.sectionId === 'availability') {
      this.preferences.write({ availability: '' });
    }
    this.syncUrl();
  }

  protected onClearAllFilters(): void {
    this.activeFilters.set({});
    this.currentPage.set(1);
    this.preferences.write({ availability: '' });
    this.syncUrl();
  }

  protected onPageChange(page: number): void {
    this.currentPage.set(page);
    this.syncUrl();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  protected onRetry(): void {
    this.fetch$.next();
  }

  protected onAddToCart(event: AddToCartEvent): void {
    this.cartService
      .addLine(Number(event.product.id), event.quantity, event.product)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.cartDrawerService.open(),
        error: (err) => {
          // `null` = bloqueo de Acceso VIP: el diálogo ya lo explica.
          const message = cartErrorMessage(err, 'No pudimos agregar el producto al carrito.');
          if (message) this.toastService.show({ type: 'error', message });
        },
      });
  }

  /**
   * Click en "Reservar" desde una ProductCard. Abre el modal de precompra
   * en vez del carrito — flujo paralelo que no toca CartService.
   */
  protected onReserve(product: Product): void {
    this.prepurchaseModalService.open(product);
  }

  /**
   * "Reservar" desde el quickview (zoom). Cierra el zoom y abre el modal de
   * precompra con la cantidad que el usuario seleccionó en el zoom.
   */
  protected onZoomReserve(event: AddToCartEvent): void {
    this.zoomProduct.set(null);
    this.prepurchaseModalService.open(event.product, event.quantity);
  }

  protected onZoomOpen(product: Product): void {
    this.zoomProduct.set(product);
  }

  protected onZoomClose(): void {
    this.zoomProduct.set(null);
  }

  protected onZoomAddToCart(event: AddToCartEvent): void {
    this.cartService
      .addLine(Number(event.product.id), event.quantity, event.product)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.cartDrawerService.open(),
        error: (err) => {
          // `null` = bloqueo de Acceso VIP: el diálogo ya lo explica.
          const message = cartErrorMessage(err, 'No pudimos agregar el producto al carrito.');
          if (message) this.toastService.show({ type: 'error', message });
        },
      });
    // Cerramos el quick-view inmediatamente (sin esperar al response) para
    // que el drawer aparezca limpio encima cuando se resuelva la llamada.
    this.zoomProduct.set(null);
  }

  protected onNotify(product: Product): void {
    this.stockAlertModal.open(product);
  }

  protected onNotifyCancel(product: Product): void {
    this.stockAlertModal.cancel(product);
  }

  protected onCardClick(product: Product): void {
    if (!product.slug) return;
    // Si estamos viendo un listing concreto (`/tienda/<categoría>`), respetar
    // ese contexto. Si estamos en `/tienda` global, derivar el slug canónico
    // del producto contra el árbol cacheado para no generar URLs como
    // `/tienda/todos/...`. Si tampoco resuelve, no navegamos.
    const fromRoute = this.category();
    const categorySlug =
      fromRoute ?? deriveProductCategorySlug(product, this.rootCategoriesSig());
    if (!categorySlug) return;
    this.router.navigate(['/tienda', categorySlug, product.slug]);
  }

  protected onViewDetails(product: Product): void {
    this.zoomProduct.set(null);
    this.onCardClick(product);
  }
}
